<template>
    <VJoyPanel :id="anchorsPageProfile.portfolioSection" class="portfolio-section" data-testid="portfolio-item-porfolio" padding="large">
        <template #panel-title>
            <h2>{{ t('profile.section.book.title') }}</h2>
        </template>
        <template #panel-title-action>
            <VJoyWrapper v-if="isEditionEnabled" no-margin>
                <PortfolioSortDialog
                    :portfolio-items="publishedItems"
                    @reorder="reorderPortfolioItems"
                    @delete-portfolio-item="deletePortfolioItem"
                />
                <ClientOnly>
                    <VJoyIconButton
                        v-if="hasPortfolioItems"
                        :label="t('profile.section.book.add.label')"
                        data-testid="portfolio-item-porfolio-add-link"
                        icon="add"
                        size="small"
                        variant="secondary"
                        @click="openPortfolioItemEditor(undefined, EditionOpenedEntryPoint.PORTFOLIO_HEADER_PLUS_ICON)"
                    />
                    <template #fallback>
                        <SectionActionSkeleton />
                    </template>
                </ClientOnly>
            </VJoyWrapper>
            <PortfolioFilterDropdown
                v-else-if="loggedIn && availableCategories.length > 0"
                v-model="selectedCategories"
                :available-categories="availableCategories"
            />
        </template>
        <template #panel-body>
            <template v-if="isEditionEnabled">
                <EmptyContentSection
                    v-if="!hasPortfolioItems"
                    :description="t('profile.section.portfolio.items.empty.description')"
                    :title="t('profile.section.portfolio.items.empty.title')"
                    icon="color"
                >
                    <template #action>
                        <ClientOnly>
                            <VJoyButton
                                data-testid="portfolio-empty-add-button"
                                icon="add"
                                size="small"
                                variant="main"
                                @click="openPortfolioItemEditor(undefined, EditionOpenedEntryPoint.PORTFOLIO_EMPTY_STATE)"
                            >
                                {{ t('profile.section.portfolio.empty.add.button') }}
                            </VJoyButton>
                            <VJoyLink
                                v-if="$ff('enable-portfolio-inspiration')"
                                href="/profile/portfolio/inspiration?entry_point=empty_state"
                                data-testid="portfolio-empty-state-inspiration-link"
                            >
                                {{ t('profile.section.portfolio.inspiration.button') }}
                            </VJoyLink>
                            <template #fallback>
                                <VJoyFormFieldSkeleton size="small" width="250px" />
                            </template>
                        </ClientOnly>
                    </template>
                </EmptyContentSection>
                <PortfolioExperienceLinkHighlight
                    v-if="isPortfolioExperienceLinkEnabled"
                    :class="hasPortfolioItems ? 'portfolio-section__highlight--top' : 'portfolio-section__highlight--bottom'"
                />
                <VJoyTabs v-if="hasPortfolioItems" :selected-tab="activeTab" @update:selected-tab="activeTab = $event">
                    <VJoyTab v-if="publishedItems.length > 0" :label="t('profile.section.portfolio.tab.published')" tab="published">
                        <PortfolioSectionFilledState
                            :is-edition-mode="isEditionEnabled"
                            :portfolio-items="publishedItems"
                            @delete="deletePortfolioItem($event)"
                            @edit="openPortfolioItemEditor($event, EditionOpenedEntryPoint.PORTFOLIO_EDIT_ITEM_PAGE)"
                            @move-to-drafts="moveToDrafts($event)"
                        />
                    </VJoyTab>
                    <VJoyTab v-if="draftItems.length > 0" :label="t('profile.section.portfolio.tab.draft')" tab="draft">
                        <PortfolioSectionFilledState
                            :is-edition-mode="isEditionEnabled"
                            :portfolio-items="draftItems"
                            @delete="deletePortfolioItem($event)"
                            @edit="openPortfolioItemEditor($event, EditionOpenedEntryPoint.PORTFOLIO_EDIT_ITEM_PAGE)"
                        />
                    </VJoyTab>
                </VJoyTabs>
            </template>
            <template v-else>
                <FakePortfolioList v-if="!loggedIn" />
                <PortfolioSectionFilledState v-else :portfolio-items="filteredPublishedItems" />
            </template>
        </template>
    </VJoyPanel>
</template>

<script lang="ts" setup>
    import {computed, ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import {navigateTo, useAuth, useProfilePageViewMode, useTranslation} from '#imports';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {
        pushVJoySnackbar,
        VJoyButton,
        VJoyFormFieldSkeleton,
        VJoyIconButton,
        VJoyLink,
        VJoyPanel,
        VJoyTab,
        VJoyTabs,
        VJoyWrapper,
    } from '@maltjoy/core-vue';
    import type {PortfolioCategory, PortfolioItemSummaryResource} from '@api';
    import EmptyContentSection from '~/components/section/EmptyContentSection.vue';
    import SectionActionSkeleton from '~/components/section/SectionActionSkeleton.vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {anchorsPageProfile} from '~/config/anchors';
    import {EditionOpenedEntryPoint, PortfolioItemStatus} from '~/config/TrackingEvents';
    import {usePortfolioItemsStore} from '~/features/portfolio/section/store/portfolio-items.store';
    import FakePortfolioList from '~/features/portfolio/section/ui/FakePortfolioList.vue';
    import PortfolioExperienceLinkHighlight from '~/features/portfolio/section/ui/PortfolioExperienceLinkHighlight.vue';
    import PortfolioFilterDropdown from '~/features/portfolio/section/ui/PortfolioFilterDropdown.vue';
    import PortfolioSectionFilledState from '~/features/portfolio/section/ui/PortfolioSectionFilledState.vue';
    import PortfolioSortDialog from '~/features/portfolio/section/ui/PortfolioSortDialog.vue';
    import {useProfileStore} from '~/store/profileStore';

    const {t} = useTranslation();
    const {trackPortfolioDeleted, trackPortfolioReordered} = useProfileTracking();
    const {profile} = storeToRefs(useProfileStore());
    const portfolioItemsStore = usePortfolioItemsStore();
    const {portfolioItems} = storeToRefs(portfolioItemsStore);
    const {loggedIn} = storeToRefs(useAuth());

    const {isEditionEnabled} = useProfilePageViewMode();
    const isPortfolioExperienceLinkEnabled = useFeatureFlag('portfolio-experience-link');
    const hasPortfolioItems = computed(() => portfolioItems.value.length > 0);
    const publishedItems = computed(() => portfolioItems.value.filter((item) => item.published));
    const draftItems = computed(() => portfolioItems.value.filter((item) => !item.published));
    const activeTab = ref(publishedItems.value.length > 0 ? 'published' : 'draft');

    watch(publishedItems, (items) => {
        if (items.length === 0) activeTab.value = 'draft';
    });
    watch(draftItems, (items) => {
        if (items.length === 0) activeTab.value = 'published';
    });

    const selectedCategories = ref<PortfolioCategory[]>([]);
    const availableCategories = computed<PortfolioCategory[]>(() => {
        const categories = publishedItems.value.map((item) => item.category).filter((c): c is PortfolioCategory => !!c);
        return [...new Set(categories)];
    });
    const filteredPublishedItems = computed(() => {
        if (selectedCategories.value.length === 0 || selectedCategories.value.length === availableCategories.value.length) {
            return publishedItems.value;
        }
        return publishedItems.value.filter((item) => item.category && selectedCategories.value.includes(item.category));
    });

    watch(availableCategories, (categories) => {
        selectedCategories.value = selectedCategories.value.filter((c) => categories.includes(c));
    });

    async function reorderPortfolioItems(sortedPortfolioItems: PortfolioItemSummaryResource[]) {
        const profileId = profile.value?.profileId;
        if (!profileId) {
            return;
        }

        const sortedPortfolioItemIds = sortedPortfolioItems.map((item) => item.id).filter((id) => !!id);
        const draftIds = draftItems.value.map((item) => item.id).filter((id) => !!id && !sortedPortfolioItemIds.includes(id));
        const orderedIds = [...sortedPortfolioItemIds, ...draftIds];
        await portfolioItemsStore.reorderPortfolioItems(profileId, orderedIds);
        trackPortfolioReordered();
        pushVJoySnackbar({props: {message: t('profile.section.portfolio.reorder.success'), level: 'success'}});
    }

    async function deletePortfolioItem(portfolioItemId: string) {
        const item = portfolioItems.value.find((i) => i.id === portfolioItemId);
        const status = item?.published ? PortfolioItemStatus.PUBLISHED : PortfolioItemStatus.DRAFT;
        await portfolioItemsStore.deletePortfolioItemById(portfolioItemId);
        trackPortfolioDeleted(portfolioItemId, status);
        pushVJoySnackbar({props: {message: t('profile.section.portfolio.delete.success'), level: 'success'}});
    }

    async function openPortfolioItemEditor(portfolioItemId?: string, entryPoint?: EditionOpenedEntryPoint) {
        await navigateTo({
            path: portfolioItemId ? `/profile/portfolio/${portfolioItemId}/edit` : '/profile/portfolio/new/edit',
            query: entryPoint ? {entryPoint} : undefined,
        });
    }

    async function moveToDrafts(portfolioItemId: string) {
        try {
            const item = await portfolioItemsStore.getPortfolioItemById(portfolioItemId);
            await portfolioItemsStore.updatePortfolioItemById(portfolioItemId, {
                title: item.title ?? '',
                description: item.description ?? '',
                contentBlocks: item.contentBlocks ?? {},
                published: false,
                tags: item.tags,
                industryExpertises: item.industryExpertises,
                category: item.category,
                period: item.period,
                thumbnail: item.thumbnail ?? '',
            });
            pushVJoySnackbar({props: {message: t('profile.section.portfolio.move.to.drafts.success'), level: 'success'}});
        } catch {
            pushVJoySnackbar({props: {message: t('generic.error.unexpected'), level: 'error'}});
        }
    }
</script>

<style lang="scss" scoped>
    .portfolio-section__highlight {
        &--top {
            margin-bottom: var(--joy-core-spacing-6);
        }

        &--bottom {
            margin-top: var(--joy-core-spacing-6);
        }
    }

    :deep(.joy-tab__selected) {
        margin-top: var(--joy-core-spacing-6);
    }
</style>
